/*
 * Copyright (c) 2023. Nedaware SL
 */

import {BasicEntity, BasicProperty} from "../../basic-entity-back/annotations";
import {PropertyType} from "../../basic-entity-back/property-type/property-type";
import {Documento} from "./documento-model";
import {PartialSearchFilter} from "../../basic-entity-back/filters/search-filter";
import {NestedBehavior} from "../../basic-entity-back/property-type/nested-model-type";
import {LineaDocumento} from "./linea-documento-model";
import {FacturaRectificativa} from "./factura-rectificativa.model";
import {BetweenDateFilter} from "../../basic-entity-back/filters/date-filter";
import {DireccionPersona} from "../personas/direccion-persona.model";
import {DescuentoGeneralFacturaRel} from "./descuento-general-factura-rel.model";
import {ManagerCachingStrategy} from "../../basic-entity-back/basic-entity-interface/mapping-external";

@BasicEntity({
    name: 'Factura',
    endpoint: 'facturas',
    isPaginated: true,
    acceptsBulkIriSearch: true,
    paginationSizes: [20, 30, 50],
    modelNameProperty: 'numeroFactura',
    type: 'Factura',
    managerCaching: ManagerCachingStrategy.Lazy
})
export class Factura extends Documento {
    static readonly ESTADOS_FACTURA = {
        PENDIENTE: 'Pendiente',
        FACTURADO: "Facturado",
    };

    @BasicProperty({
        name: 'Estado',
        type: PropertyType.Choice(...Object.values(Factura.ESTADOS_FACTURA)),
        sortable: true
    })
    private _estado;

    @BasicProperty({
        name: 'Líneas',
        type: PropertyType.NestedModel(LineaDocumento, null, NestedBehavior.CreateOnly),
        array: true
    })
    private _lineasFactura: LineaDocumento[];

    @BasicProperty({
        name: 'Facturas rectificativas',
        type: PropertyType.NestedModel(FacturaRectificativa, 'factura', NestedBehavior.CreateOnly),
        array: true,
        nullable: true
    })
    private _facturasRectificativas: FacturaRectificativa[];

    @BasicProperty({
        name: 'Fecha',
        type: PropertyType.Date(),
        filters: [BetweenDateFilter]
    })
    private _fecha;

    @BasicProperty({
        name: 'Número de factura',
        type: PropertyType.String(),
        filters: [PartialSearchFilter]
    })
    private _numeroFactura;

    @BasicProperty({
        name: 'Dirección de facturación',
        type: PropertyType.Uri(DireccionPersona)
    })
    private _direccionFacturacion;

    @BasicProperty({
        name: 'Descuentos genéricos aplicados',
        type: PropertyType.NestedModel(DescuentoGeneralFacturaRel, null, NestedBehavior.CreateOnly),
        array: true
    })
    private _descuentosGeneralesAplicados: DescuentoGeneralFacturaRel[] = [];

    get estado() {
        return this._estado;
    }

    set estado(value) {
        this._estado = value;
    }

    get lineasFactura(): LineaDocumento[] {
        return this._lineasFactura;
    }

    set lineasFactura(value: LineaDocumento[]) {
        this._lineasFactura = value;
    }

    get fecha() {
        return this._fecha;
    }

    set fecha(value) {
        this._fecha = value;
    }

    get numeroFactura() {
        return this._numeroFactura;
    }

    set numeroFactura(value) {
        this._numeroFactura = value;
    }

    get facturasRectificativas(): FacturaRectificativa[] {
        return this._facturasRectificativas;
    }

    set facturasRectificativas(value: FacturaRectificativa[]) {
        this._facturasRectificativas = value;
    }

    get direccionFacturacion() {
        return this._direccionFacturacion;
    }

    set direccionFacturacion(value) {
        this._direccionFacturacion = value;
    }

    get descuentosGeneralesAplicados(): DescuentoGeneralFacturaRel[] {
        return this._descuentosGeneralesAplicados;
    }

    set descuentosGeneralesAplicados(value: DescuentoGeneralFacturaRel[]) {
        this._descuentosGeneralesAplicados = value;
    }
}

// LineaDocumento.MAP_FACTURA.type = PropertyType.Uri(Factura);
FacturaRectificativa.MAP_FACTURA.type = PropertyType.Uri(Factura);
